import {useCallback} from "react";
import {call, delay, fork, race, take} from "redux-saga/effects";

import {putFeedback} from "../store/feedback";
import {actions, signalReady, useSagaCallback} from "../store/sagas";
import {apiClient, clearApiCredentials, loadApiCredentials, setApiCredentials} from './client';
import {loadUserProfile} from "./user";
import {globalSignOn} from "./sso";

async function signIn(username, password) {
    const body = new URLSearchParams();
    body.set('username', username);
    body.set('password', password);
    const { data } = await apiClient.request({
        method: "POST",
        url: `/api/v2/user/login`,
        withCredentials: true,
        data: body,
        headers: {}
    });
    if (!data.status) {
        throw new Error(data.error);
    }
    return data;
}

export async function getToken() {
    const resp = await apiClient.request({
        method: "POST",
        url: `/api/v2/token`,
        withCredentials: true,
        headers: {}
    });
    const {status, error, api_key, api_secret} = resp.data;
    if (typeof status === 'boolean') {
        // new API status style
        if (status === false) {
            throw new Error(error);
        }
    } else if (status !== 'success') {
        // old API status style
        throw new Error(status);
    }
    return [api_key, api_secret];
}

function* waitUntilSessionCreated() {
    while (true) {
        const {payload: {message}} = yield take('windowMessageReceived');
        if (message === 'session-create') {
            const [key, secret] = yield call(getToken);
            setApiCredentials(key, secret);
            return;
        }
    }
}

export function* autoSignIn() {
    const sources = [
        'localStorage',
        'cookie',
        'passive'
    ];
    for (const source of sources) {
        try {
            if (source === 'localStorage') {
                if (!loadApiCredentials()) {
                    continue;
                }
            } else if (source === 'cookie') {
                const [key, secret] = yield call(getToken);
                setApiCredentials(key, secret);
            } else if (source === 'passive') {
                // Allow API requests to proceed without credentials
                // while passive login happens in the background.
                yield call(signalReady);
                let outcome;
                yield actions.passiveLoginChanged(true);
                try {
                    outcome = yield race({
                        done: call(waitUntilSessionCreated),
                        timeout: delay(60000),
                    });
                } finally {
                    yield actions.passiveLoginChanged(false);
                }
                if (outcome.timeout) {
                    continue;
                }
            }
            yield call(loadUserProfile);
            // User is positively signed in.
            return true;
        } catch {
            // try next source
        }
    }
    // User is positively signed out.
    yield call(clearApiCredentials);
    return false;
}

export const useSignIn = (feedbackCh) => {
    return useSagaCallback(feedbackCh, useCallback(function* (username, password) {
        yield actions.autoSignInCanceled();
        yield putFeedback({busy: true});
        try {
            const result = yield call(signIn, username, password);
            // TODO: error handling?
            if (result.sso_token) {
                yield fork(globalSignOn, result.sso_token);
            }
            const [key, secret] = yield call(getToken);
            setApiCredentials(key, secret);
        } finally {
            yield putFeedback({busy: false});
        }
        yield call(loadUserProfile);
    }, []));
};
